import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import EllipsisTypography from '../EllipsisTypography/EllipsisTypography';
import NRSeverityLabel from '../NRSeverityLabel/NRSeverityLabel';
import { CONSTANTS } from '../../constants/constants';
import {
  camelCaseToHuman,
  capitalizeFirstLetter,
  formatCvssScore,
  formatEPSSValue,
  formatExploitMaturityValue,
  formatSeverityValue
} from '../../utils/utils';

const { ACTIVITY_UPDATE_FIELD_LABELS } = CONSTANTS.VULNERABILITIES;

export function getVulnerabilityEntityMetaLines(entityMeta) {
  if (!entityMeta) return [];

  const lines = [];
  if (entityMeta.severity) {
    lines.push({
      key: 'severity',
      label: ACTIVITY_UPDATE_FIELD_LABELS.severity,
      value: formatSeverityValue(entityMeta.severity),
      severity: entityMeta.severity,
      cvssScore: entityMeta.cvssScore
    });
  } else if (entityMeta.cvssScore != null && entityMeta.cvssScore !== '') {
    lines.push({
      key: 'cvss_score',
      label: ACTIVITY_UPDATE_FIELD_LABELS.cvss_score,
      value: formatCvssScore(entityMeta.cvssScore)
    });
  }
  if (entityMeta.epssScore != null && entityMeta.epssScore !== '') {
    lines.push({
      key: 'epss_score',
      label: ACTIVITY_UPDATE_FIELD_LABELS.epss_score,
      value: formatEPSSValue(entityMeta.epssScore)
    });
  }
  if (entityMeta.knownExploited) {
    lines.push({
      key: 'known_exploited',
      label: ACTIVITY_UPDATE_FIELD_LABELS.cisa_kev_prevalence,
      value: 'Yes'
    });
  }
  if (entityMeta.exploitMaturity) {
    lines.push({
      key: 'exploit_maturity',
      label: ACTIVITY_UPDATE_FIELD_LABELS.exploit_maturity,
      value: formatExploitMaturityValue(entityMeta.exploitMaturity)
    });
  }
  return lines;
}

export function getActivityFeedSecondaryLineCount(item) {
  const entity = item?.entity;
  if (!entity) return 0;

  switch (entity.__typename) {
    case 'VulnerabilityUpdatedPayload':
      return entity.vulnerabilityUpdates?.length ?? 0;
    case 'VulnerabilityAddedPayload':
    case 'VulnerabilityRemovedPayload':
      return getVulnerabilityEntityMetaLines(entity.entity).length + (item?.description || item?.user ? 1 : 0);
    case 'VulnerabilityRemediatedPayload': {
      let count = 0;
      if (entity.severity || entity.componentName) count += 1;
      if (entity.vexStatus && entity.vexStatus !== 'UNSPECIFIED') count += 1;
      if (item?.description || item?.user) count += 1;
      return count;
    }
    case 'GroupMembershipChangedPayload': {
      const added = entity.membersAdded?.length ?? 0;
      const removed = entity.membersRemoved?.length ?? 0;
      return (added > 0 ? 1 : 0) + (removed > 0 ? 1 : 0) + (item?.description || item?.user ? 1 : 0);
    }
    case 'IdentificationAddedPayload':
    case 'IdentificationRemovedPayload':
      return (entity.remediationAction ? 1 : 0) + (item?.description || item?.user ? 1 : 0);
    case 'GroupCreatedPayload':
    case 'GroupUpdatedPayload':
    case 'GroupDeletedPayload':
      return (entity.description ? 1 : 0) + (item?.description || item?.user ? 1 : 0);
    case 'AssetAnalysisCompletedPayload':
      return (entity.revision != null ? 1 : 0) + (item?.description || item?.user ? 1 : 0);
    default:
      return item?.description || item?.user ? 1 : 0;
  }
}

export function getFallbackHeaderLabel(item) {
  const activityTypeParts = item?.activityType?.split('_') ?? [];
  return activityTypeParts.slice(0, -1).map(capitalizeFirstLetter).join(' ');
}

export function getActivityVerb(item) {
  const type = item?.activityType;
  switch (type) {
    case 'ASSET_ANALYSIS_COMPLETED':
      return item?.entity?.failure ? 'failed' : 'completed';
    case 'GROUP_MEMBERSHIP_CHANGED':
      return 'membership changed';
    case 'ORG_SETTINGS_UPDATED':
      return 'updated';
    case 'VULNERABILITY_REMEDIATED':
    case 'SECRET_REMEDIATED':
    case 'LICENSE_ISSUE_REMEDIATED':
      return 'remediated';
    default:
      return type?.split('_').at(-1)?.toLowerCase() ?? '';
  }
}

export function HeaderBlock({ primary, secondary }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '100%' }}>
      {primary}
      {secondary}
    </Box>
  );
}

export function DefaultPrimaryHeader({ label, activity, linkProps, endAdornment }) {
  const theme = useTheme();
  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <EllipsisTypography
        {...(linkProps || {})}
        variant="body2"
        sx={{
          wordBreak: 'break-all',
          ...(linkProps ? { color: theme.palette.blue.link, cursor: 'pointer' } : {})
        }}
      >
        {label}
      </EllipsisTypography>
      <Typography sx={{ ml: 0.5, mr: 1 }} variant="body2" children={activity} />
      {endAdornment}
    </Box>
  );
}

export function DescriptionSecondary({ item }) {
  if (!item?.description && !item?.user) return null;
  return (
    <EllipsisTypography variant="body2" sx={{ wordBreak: 'break-all' }} color="text.secondary">
      {item?.description}
      {!!item?.user ? ` by ${item?.user}` : ''}
    </EllipsisTypography>
  );
}

export function MetaLine({ children }) {
  return (
    <Typography variant="body2" color="text.secondary">
      {children}
    </Typography>
  );
}

export function StatusSecondary({ previous, current }) {
  return (
    <EllipsisTypography variant="body2" sx={{ wordBreak: 'break-all' }} color="text.secondary">
      {`Status: ${camelCaseToHuman(previous || '', { preserveAcronyms: false })} → ${camelCaseToHuman(current || '', {
        preserveAcronyms: false
      })}`}
    </EllipsisTypography>
  );
}

export function VulnerabilityEntitySecondary({ entityMeta, item }) {
  const lines = getVulnerabilityEntityMetaLines(entityMeta);
  if (!lines.length && !item?.description && !item?.user) return null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
      {lines.map(line =>
        line.key === 'severity' ? (
          <Box key={line.key} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              {`${line.label}:`}
            </Typography>
            <NRSeverityLabel severity={line.severity} cvssScore={line.cvssScore} />
          </Box>
        ) : (
          <MetaLine key={line.key}>{`${line.label}: ${line.value}`}</MetaLine>
        )
      )}
      <DescriptionSecondary item={item} />
    </Box>
  );
}
